import React, { useEffect, useState } from 'react';
import { Lightbulb, Upload } from 'lucide-react';
import { apiCall } from '../_shared';
import { Card, eur, pct } from './StatsWidgets';
import { RatePushModal } from './RatePushModal';

const TONE = {
  raise: 'bg-emerald-100 text-emerald-900 border-emerald-300',
  hold: 'bg-slate-50 text-slate-700 border-slate-200',
  lower: 'bg-rose-100 text-rose-900 border-rose-300',
  promote: 'bg-amber-100 text-amber-900 border-amber-300',
};
const LABEL = { raise: 'Raise', hold: 'Hold', lower: 'Lower', promote: 'Promote' };

const MonthCell = ({ c }) => {
  const sg = c.suggestion;
  if (!sg) return <td className="p-1 text-center text-[10px] text-slate-300">closed</td>;
  const title = `${sg.reason}${c.ly_occupancy != null ? ` · LY ${c.ly_occupancy}% / €${c.ly_adr ?? '—'}` : ''}`;
  return (
    <td className="p-1" title={title}>
      <div className={`rounded border px-1.5 py-1 text-center ${TONE[sg.action]}`}>
        <p className="text-[11px] font-bold leading-tight">{pct(c.occupancy)}</p>
        <p className="text-[10px] leading-tight">€{c.adr == null ? '—' : Math.round(c.adr)}</p>
        <p className="text-[9px] uppercase font-semibold mt-0.5">{LABEL[sg.action]}{sg.pct ? ` ${sg.action === 'lower' ? '−' : '+'}${sg.pct}%` : ''}</p>
      </div>
    </td>
  );
};

const RoomRow = ({ r }) => {
  const cells = (r.cells || []).map((c) => <MonthCell key={c.m} c={c} />);
  return (
    <tr className="border-t align-top" data-testid={`price-room-${r.room}`}>
      <td className="p-1 pr-2 text-xs"><p className="font-semibold text-slate-900 leading-tight">{r.room}</p><p className="text-[10px] text-slate-500">{r.units} units · season {pct(r.season_occupancy)} · ADR {eur(r.season_adr)}</p></td>
      {cells}
      <td className="p-1 text-center"><span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${TONE[r.verdict]}`} data-testid={`price-verdict-${r.room}`}>{LABEL[r.verdict]}</span></td>
    </tr>
  );
};

export const PriceInsightCard = ({ year }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [apply, setApply] = useState(null);
  useEffect(() => {
    let alive = true;
    setData(null);
    apiCall(`/api/admin/stats/price-insights?year=${year}`).then((d) => { if (alive) setData(d); }).catch((e) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [year]);
  if (err) return <Card title="Price insight" testId="price-insight"><p className="text-xs text-red-700">{err}</p></Card>;
  if (!data) return <Card title="Price insight" testId="price-insight"><p className="text-xs text-slate-500">Comparing rates with occupancy…</p></Card>;
  const months = data.season_months || [];
  const propMap = {};
  (data.property || []).forEach((p) => { propMap[p.month] = p; });
  const heads = months.map((m) => <th key={m} className="p-1 text-center">{m}<br /><span className="font-normal text-slate-400">{pct(propMap[m]?.occupancy)} · €{propMap[m]?.adr == null ? '—' : Math.round(propMap[m].adr)}</span></th>);
  const rows = (data.rooms || []).map((r) => <RoomRow key={r.room} r={r} />);
  const actions = (data.actions || []).slice(0, 8).map((a) => {
    const signed = a.action === 'lower' ? -a.pct : a.pct;
    const pushable = (a.action === 'raise' || a.action === 'lower') && a.pct;
    return (
      <li key={`${a.room}-${a.m}`} className="flex items-start gap-2 text-xs" data-testid={`price-action-${a.m}-${a.room}`}>
        <span className={`shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-bold uppercase ${TONE[a.action]}`}>{LABEL[a.action]} {a.pct ? `${a.action === 'lower' ? '−' : '+'}${a.pct}%` : ''}</span>
        <span className="text-slate-700 flex-1"><b>{a.room}</b> · {a.month}: {a.reason}{a.impact ? <span className="text-slate-500"> · ≈ {eur(a.impact)}</span> : null}</span>
        {pushable && <button type="button" onClick={() => setApply({ room: a.room, month: a.m, monthLabel: a.month, pct: signed })} className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold bg-slate-900 text-white rounded px-2 py-1 hover:bg-slate-700" data-testid={`price-apply-${a.m}-${a.room}`}><Upload className="w-3 h-3" /> Apply</button>}
      </li>
    );
  });
  const sub = `Season ${months[0] || ''}–${months[months.length - 1] || ''} ${data.year} · cell = occupancy / ADR achieved · header = whole hotel · rules: ≥${data.rules.raise_above}% raise, ${data.rules.good_above}–${data.rules.raise_above}% hold, <${data.rules.low_below}% lower or promote`;
  return (
    <Card title="Price insight — rate vs. occupancy per room type" sub={sub} testId="price-insight" right={<span className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 whitespace-nowrap" data-testid="price-potential">≈ {eur(data.potential)} upside from raises</span>}>
      <div className="grid xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 overflow-x-auto">
          <table className="w-full text-xs" data-testid="price-table">
            <thead><tr className="text-[10px] uppercase text-slate-500"><th className="p-1 text-left">Room type</th>{heads}<th className="p-1">Verdict</th></tr></thead>
            <tbody>{rows}</tbody>
          </table>
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1"><Lightbulb className="w-3.5 h-3.5 text-amber-500" /> Biggest moves first</p>
          <ul className="space-y-1.5" data-testid="price-actions">{actions}</ul>
          {!actions.length && <p className="text-xs text-slate-400">Nothing to change — every type is in its healthy band.</p>}
          <p className="text-[10px] text-slate-400 mt-2">Apply = change Daily Price 1 by that % in Beds24 for the next {months.length ? '' : ''}upcoming occurrence of the month, after a preview.</p>
        </div>
      </div>
      {apply && <RatePushModal title={`${apply.room} · ${apply.monthLabel} · ${apply.pct > 0 ? '+' : ''}${apply.pct}%`} request={{ path: '/api/admin/rates/apply-suggestion', body: { room: apply.room, month: apply.month, pct: apply.pct } }} onClose={() => setApply(null)} />}
    </Card>
  );
};
